function deepFreeze(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
}

/** An immutable machine definition. Owns asset selection and localized content. */
export default class Equipment {
  constructor(definition) {
    if (!definition.id || !definition.path || !definition.name) {
      throw new Error('Equipment requires an id, path, and name.');
    }
    if (definition.catalog && !definition.catalog.files[definition.catalog.fallbackLanguage]) {
      throw new Error(`Missing fallback catalog for ${definition.id}.`);
    }
    this.definition = deepFreeze(definition);
    Object.freeze(this);
  }

  get id() { return this.definition.id; }
  get path() { return this.definition.path; }
  get translations() { return this.definition.translations; }

  assetUrl(file) {
    return `${process.env.PUBLIC_URL || ''}${this.definition.assetDirectory}/${file}`;
  }

  catalogUrl(language) {
    const { catalog } = this.definition;
    if (!catalog) return null;
    const code = String(language || '').toLowerCase().split(/[-_]/)[0];
    return this.assetUrl(catalog.files[code] || catalog.files[catalog.fallbackLanguage]);
  }

  videoUrl(language) {
    const { video } = this.definition;
    if (!video) return null;
    const code = String(language || '').toLowerCase().split(/[-_]/)[0];
    const file = video.files?.[code]
      || video.files?.[video.fallbackLanguage || 'en']
      || video.file;
    return this.assetUrl(file);
  }

  /** t is scoped to this machine; shared labels are supplied by the UI. */
  present(t, language) {
    const data = this.definition;
    const text = item => item.valueKey ? t(item.valueKey) : item.value;
    const articles = (ids, group) => ids.map(id => {
      const imageFile = group === 'options' ? data.optionImages?.[id] : null;
      return {
        id, title: t(`${group}.${id}.title`), description: t(`${group}.${id}.description`),
        image: imageFile ? this.assetUrl(`options/${imageFile}`) : null,
      };
    });
    return {
      id: this.id,
      path: this.path,
      name: data.name,
      specificationsAnchor: data.specificationsAnchor || `${this.id}-specs`,
      displayName: data.displayName || { main: data.name },
      category: t('category'),
      description: t('description'),
      headline: t('headline'),
      image: { ...data.image, src: this.assetUrl(data.image.file), alt: t('imageAlt') },
      badges: data.badges.map(item => ({ id: item.id, text: text(item) })),
      metrics: data.metrics.map(item => ({ ...item, label: item.labelKey ? t(item.labelKey) : null })),
      features: {
        eyebrow: t('featuresLabel'), title: t('featuresTitle'),
        items: articles(data.features, 'features'),
      },
      video: data.video ? {
        src: this.videoUrl(language), poster: this.assetUrl(data.video.poster),
        eyebrow: t('filmLabel'), title: t('filmTitle'), description: t('filmDescription'),
      } : null,
      specifications: data.specifications.map(item => ({
        id: item.id, label: t(`specifications.${item.id}.label`),
        value: item.value ?? t(`specifications.${item.id}.value`),
      })),
      specificationNote: data.specificationNoteKey ? t(data.specificationNoteKey) : null,
      options: { title: t('optionsTitle'), items: articles(data.options, 'options') },
      catalog: data.catalog ? {
        src: this.catalogUrl(language), summary: t('catalogSummary'), alt: t('catalogAlt'),
      } : null,
    };
  }
}
